import { useMemo, useState } from 'react';
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	X,
	Navigation,
	Bot,
	Clock3,
	LocateFixed,
	PenLine,
	RefreshCw,
	Wallet,
} from 'lucide-react';

import { AppContent, AppSection, AppStack } from '@/components/layout/AppLayout';
import { ScreenBody } from '@/components/ScreenBody';
import { Button } from '@/components/shadcn-ui/Button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/shadcn-ui/Dialog';
import { Input } from '@/components/shadcn-ui/Input';
import { Label } from '@/components/shadcn-ui/Label';
import { useToast } from '@/components/shadcn-ui/use-toast';
import { useTaxiData } from '@/contexts/TaxiDataContext';
import {
	buildGoogleMapsLink,
	extractDestinationFromSuggestion,
	formatTimestamp,
} from '@/lib/taxiAnalytics';
import { TaxiLog } from '@/types/taxi';

const TaxiInputDashboard = () => {
	const { toast } = useToast();
	const {
		logs,
		activeGpsSession,
		suggestion,
		isSuggesting,
		createLog,
		removeLogById,
		startGpsSession,
		endGpsSession,
		refreshSuggestion,
	} = useTaxiData();

	const [revenue, setRevenue] = useState('');
	const [memo, setMemo] = useState('');
	const [isRecordingPickup, setIsRecordingPickup] = useState(false);
	const [lastPickupLog, setLastPickupLog] = useState<TaxiLog | null>(null);
	const [isSubmittingPickup, setIsSubmittingPickup] = useState(false);
	const [isSubmittingDropoff, setIsSubmittingDropoff] = useState(false);
	const [isDropoffModalOpen, setIsDropoffModalOpen] = useState(false);

	const recentLogs = useMemo(() => logs.slice(0, 5), [logs]);
	const destination = useMemo(
		() => extractDestinationFromSuggestion(suggestion, '新宿駅東口'),
		[suggestion],
	);
	const googleMapsLink = useMemo(
		() => buildGoogleMapsLink(destination),
		[destination],
	);

	const handlePickup = async () => {
		setIsSubmittingPickup(true);
		try {
			const createdLog = await createLog({
				event: 'pickup',
				revenue: null,
				memo: '',
			});
			setLastPickupLog(createdLog);
			setIsRecordingPickup(true);
			toast({
				title: '記録しました',
				description: '乗車データを記録しました',
			});
		} catch (error) {
			toast({
				title: '保存に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		} finally {
			setIsSubmittingPickup(false);
		}
	};

	const handleCancelPickup = () => {
		if (!lastPickupLog || !isRecordingPickup) return;
		removeLogById(lastPickupLog.id);
		setLastPickupLog(null);
		setIsRecordingPickup(false);
		setRevenue('');
		setMemo('');
		setIsDropoffModalOpen(false);
		toast({
			title: 'キャンセルしました',
			description: '直前の乗車データを削除しました',
		});
	};

	const handleDropoff = async () => {
		const parsedRevenue = Number(revenue.trim());
		if (!revenue.trim() || Number.isNaN(parsedRevenue) || parsedRevenue <= 0) {
			toast({
				title: '入力エラー',
				description: '降車時は金額の入力が必須です',
				variant: 'destructive',
			});
			return;
		}

		setIsSubmittingDropoff(true);
		try {
			await createLog({
				event: 'dropoff',
				revenue: parsedRevenue,
				memo,
			});
			setRevenue('');
			setMemo('');
			setLastPickupLog(null);
			setIsRecordingPickup(false);
			setIsDropoffModalOpen(false);
			toast({
				title: '記録しました',
				description: '降車データを記録しました',
			});
		} catch (error) {
			toast({
				title: '保存に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		} finally {
			setIsSubmittingDropoff(false);
		}
	};

	const handleStartGps = async () => {
		try {
			await startGpsSession();
			toast({
				title: '待機開始',
				description: '始点を記録しました',
			});
		} catch (error) {
			toast({
				title: '待機開始に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		}
	};

	const handleEndGps = async () => {
		try {
			await endGpsSession();
			toast({
				title: '待機終了',
				description: '終点を記録しました',
			});
		} catch (error) {
			toast({
				title: '待機終了に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		}
	};

	const handleRefreshSuggestion = async () => {
		try {
			await refreshSuggestion();
		} catch (error) {
			toast({
				title: '提案更新に失敗しました',
				description: (error as Error).message,
				variant: 'destructive',
			});
		}
	};

	return (
		<ScreenBody>
			<AppContent>
				<AppStack className="pb-3">
					<AppSection>
						<div className="mb-3 flex items-center justify-between">
							<div>
								<h1 className="text-lg font-bold">運行入力</h1>
								<p className="text-xs text-slate-500">最短操作で記録</p>
							</div>
							<div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium">
								{logs.length} 件
							</div>
						</div>

						<div className="space-y-3">
							<div className="grid grid-cols-1 gap-2">
								{isRecordingPickup ? (
									<>
										<Button
											type="button"
											onClick={() => setIsDropoffModalOpen(true)}
											disabled={isSubmittingDropoff}
											className="h-12 border-2 border-rose-600 bg-rose-600 text-base text-white hover:bg-rose-700"
										>
											<ArrowDownToLine className="mr-1 h-4 w-4" />
											降車
										</Button>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={handleCancelPickup}
											className="h-8 justify-center text-xs text-slate-600 hover:text-slate-900"
										>
											<X className="mr-1 h-3 w-3" />
											キャンセル
										</Button>
									</>
								) : (
									<Button
										type="button"
										onClick={handlePickup}
										disabled={isSubmittingPickup}
										variant="outline"
										className="h-12 border-2 border-emerald-600 bg-emerald-600 text-base text-white hover:bg-emerald-700"
									>
										<ArrowUpFromLine className="mr-1 h-4 w-4" />
										{isSubmittingPickup ? '記録中...' : '乗車'}
									</Button>
								)}
							</div>

							{isRecordingPickup ? (
								<p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
									「降車」をタップすると運賃とメモの入力モーダルが開きます。
								</p>
							) : (
								<p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
									「乗車」をタップした時点で記録されます。降車時に運賃とメモを入力してください。
								</p>
							)}
						</div>
					</AppSection>

					<AppSection>
						<div className="mb-2 flex items-center gap-1 text-sm font-semibold">
							<LocateFixed className="h-4 w-4 text-slate-500" />
							待機GPS
						</div>
						{activeGpsSession ? (
							<div className="space-y-2">
								<div className="text-xs text-slate-600">
									開始: {formatTimestamp(activeGpsSession.startTimestamp)}
								</div>
								<Button onClick={handleEndGps} className="h-12 w-full text-base">
									待機終了を記録
								</Button>
							</div>
						) : (
							<Button
								onClick={handleStartGps}
								className="h-12 w-full text-base"
								variant="outline"
							>
								待機開始を記録
							</Button>
						)}
					</AppSection>

					<AppSection className="bg-slate-50">
						<div className="mb-2 flex items-center justify-between">
							<div className="flex items-center gap-1 text-sm font-semibold">
								<Bot className="h-4 w-4 text-slate-600" />
								次の待機指示
							</div>
							<Button
								size="sm"
								variant="outline"
								disabled={isSuggesting}
								onClick={handleRefreshSuggestion}
							>
								<RefreshCw className="mr-1 h-4 w-4" />
								更新
							</Button>
						</div>
						<p className="text-sm leading-relaxed text-slate-700">{suggestion}</p>
						<a
							href={googleMapsLink}
							target="_blank"
							rel="noreferrer"
							className="mt-3 block"
						>
							<Button className="h-11 w-full" variant="outline">
								<Navigation className="mr-2 h-4 w-4" />
								{destination} を Google Maps で開く
							</Button>
						</a>
					</AppSection>

					<AppSection>
						<div className="mb-2 flex items-center gap-1 text-sm font-semibold">
							<Clock3 className="h-4 w-4 text-slate-500" />
							最新ログ
						</div>
						{recentLogs.length === 0 ? (
							<p className="text-sm text-slate-500">まだログがありません</p>
						) : (
							<ul className="space-y-2">
								{recentLogs.map((log) => (
									<li key={log.id} className="rounded-xl border bg-slate-50 p-2 text-xs">
										<div className="font-medium">
											{formatTimestamp(log.timestamp)} / {log.event}
										</div>
										<div className="text-slate-600">
											¥{log.revenue?.toLocaleString() || '-'} ・ {log.memo || 'メモなし'}
										</div>
									</li>
								))}
							</ul>
						)}
					</AppSection>
				</AppStack>
			</AppContent>
			<Dialog
				open={isDropoffModalOpen}
				onOpenChange={(open) => {
					if (!open && !isSubmittingDropoff) {
						setIsDropoffModalOpen(false);
					} else if (open) {
						setIsDropoffModalOpen(true);
					}
				}}
			>
				<DialogContent className="w-[92vw] max-w-md rounded-xl p-4 sm:p-6">
					<DialogHeader>
						<DialogTitle>降車を記録</DialogTitle>
						<DialogDescription>
							運賃は必須です。必要ならメモも入力してください。
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						<div className="space-y-1">
							<Label htmlFor="dropoff-revenue" className="flex items-center gap-1">
								<Wallet className="h-4 w-4 text-slate-500" />
								運賃（必須）
							</Label>
							<Input
								id="dropoff-revenue"
								inputMode="numeric"
								placeholder="例: 1680"
								value={revenue}
								onChange={(e) => setRevenue(e.target.value)}
								className="h-11 text-base"
								autoFocus
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor="dropoff-memo" className="flex items-center gap-1">
								<PenLine className="h-4 w-4 text-slate-500" />
								メモ（任意）
							</Label>
							<Input
								id="dropoff-memo"
								placeholder="例: zone:新宿駅東口 雨"
								value={memo}
								onChange={(e) => setMemo(e.target.value)}
								className="h-11 text-base"
							/>
						</div>
					</div>
					<DialogFooter className="gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => setIsDropoffModalOpen(false)}
							disabled={isSubmittingDropoff}
						>
							閉じる
						</Button>
						<Button
							type="button"
							onClick={handleDropoff}
							disabled={isSubmittingDropoff}
							className="bg-rose-600 text-white hover:bg-rose-700"
						>
							{isSubmittingDropoff ? '記録中...' : '降車を記録'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</ScreenBody>
	);
};

export default TaxiInputDashboard;
